
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Collection } from "@/lib/api/types";
import { StatusBadges } from "./stats/StatusBadges";
import { ExportButton } from "./stats/ExportButton";
import { CollectionDownloadButton } from "./CollectionDownloadButton";
import { useCollectionExport } from "./hooks/useCollectionExport";

export interface CollectionStats {
  collection_id: string;
  total_count: number;
  pending_count: number;
  all_time_count: number;
}

export const useCollectionStats = (collections: Collection[]) => {
  return useQuery({
    queryKey: ["collectionStats", collections.map(c => c.id).sort().join(",")],
    queryFn: async () => {
      if (!collections.length) return [];

      const ids = collections.map(c => c.id);
      console.log('[CollectionCardStats] Fetching RPC stats for collections:', ids);

      const { data, error } = await supabase.rpc("get_collection_product_counts", {
        p_collection_ids: ids,
      });

      if (error) {
        console.error('[CollectionCardStats] RPC error:', error);
        throw error;
      }

      const countsMap = new Map<string, { total: number; pending: number }>();
      (data || []).forEach((row: any) => {
        countsMap.set(row.collection_id, {
          total: row.total || 0,
          pending: row.pending || 0,
        });
      });

      const stats = ids.map((id) => {
        const row = countsMap.get(id) || { total: 0, pending: 0 };
        return {
          collection_id: id,
          total_count: row.total,
          pending_count: row.pending,
          all_time_count: row.total, // fallback to total to avoid extra heavy queries
        };
      });

      return stats as CollectionStats[];
    },
    enabled: collections.length > 0,
    staleTime: 30000,
    gcTime: 120000,
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
  });
};

interface CollectionCardStatsProps {
  collection: Collection;
  stats?: CollectionStats;
  collectionId: string;
}

export const CollectionCardStats = ({ collection, stats, collectionId }: CollectionCardStatsProps) => {
  const { descriptions, handleExport } = useCollectionExport(collection.id);
  const isFullyApproved = stats?.total_count > 0 && stats?.pending_count === 0;

  return (
    <div className="space-y-3">
      <StatusBadges stats={stats} collectionId={collectionId} />
      {isFullyApproved && (
        <div className="pt-2">
          <CollectionDownloadButton 
            collectionId={collection.id}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};
